import { orchestrator } from 'satcheljs';
import {
    updateRequiredAttendees,
    updateDateTimes,
    updateLocations,
    logQuickComposePivotType,
    onReminderChanged,
    updateFreeBusy,
    updateAllDay,
    onCalendarIdChanged,
    setRecurrence,
} from 'owa-calendar-compose-form-actions';
import {
    updateQuickComposePivotContent,
    updateCachedEventData,
    setQuickComposeEventBodyViewState,
    updateOofStartEndDates,
} from '../actions/internalActions';
import { lazyNavigateToOofPivot } from '../contentsIndex';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import { QuickComposePivotType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import getQuickComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getQuickComposeViewState';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { getCalendarIdByFolderId } from 'owa-calendar-cache';
import createEditorViewState from 'owa-editor/lib/utils/createEditorViewState';

orchestrator(updateQuickComposePivotContent, actionMessage => {
    const { event } = getComposeStore();
    const { pivot, previousPivot } = actionMessage;
    logQuickComposePivotType(pivot);
    const viewState = getQuickComposeViewState();

    cacheCalendarEventData(event, previousPivot, viewState.bodyViewState);

    switch (pivot) {
        case QuickComposePivotType.Event:
            updateEventPivotContent(event, viewState.cachedEventData);
            break;

        case QuickComposePivotType.OOF:
            lazyNavigateToOofPivot.importAndExecute();
            break;
    }
});

function cacheCalendarEventData(
    event: CalendarEvent,
    previousPivot: QuickComposePivotType,
    bodyViewState: EditorViewState
) {
    if (previousPivot === QuickComposePivotType.OOF) {
        updateOofStartEndDates(event.Start, event.End);
        return;
    }

    let data: Partial<CalendarEvent> = {};

    if (previousPivot === QuickComposePivotType.Event) {
        data.RequiredAttendees = event.RequiredAttendees;
        data.Body = { Value: bodyViewState.content, BodyType: 'HTML' };
        data.Locations = [...event.Locations];
        data.Start = event.Start;
        data.End = event.End;
        data.IsAllDayEvent = event.IsAllDayEvent;
        data.ReminderMinutesBeforeStart = event.ReminderMinutesBeforeStart;
        data.ReminderIsSet = event.ReminderIsSet;
        data.FreeBusyType = event.FreeBusyType;
        data.ParentFolderId = event.ParentFolderId;
    }

    updateCachedEventData(data);
}

function updateEventPivotContent(event: CalendarEvent, cachedEventData: Partial<CalendarEvent>) {
    // required attendees
    if (cachedEventData.RequiredAttendees) {
        updateRequiredAttendees(cachedEventData.RequiredAttendees);
    }

    // body
    if (cachedEventData.Body) {
        setQuickComposeEventBodyViewState(
            createEditorViewState(cachedEventData.Body.Value, true /*isDirty*/)
        );
    }

    // start/end time
    restoreEventTime(cachedEventData);

    // all day event
    updateAllDay(cachedEventData.IsAllDayEvent);

    // locations
    if (cachedEventData.Locations) {
        updateLocations(cachedEventData.Locations);
    }

    // clear recurrence
    setRecurrence(null /*recurrenceType*/);

    // set reminder leading time
    onReminderChanged(cachedEventData.ReminderMinutesBeforeStart);

    // set busy status
    updateFreeBusy(cachedEventData.FreeBusyType);

    // calendar id
    onCalendarIdChanged(getCalendarIdByFolderId(cachedEventData.ParentFolderId.Id));
}

function restoreEventTime(cachedEventData: Partial<CalendarEvent>) {
    if (cachedEventData.Start && cachedEventData.End) {
        updateDateTimes({ start: cachedEventData.Start, end: cachedEventData.End });
    }
}
